import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import { SmartSort } from '@iheartjane/dm-sdk';
import type { SmartSortRotatorSnapshot } from '@iheartjane/utils';
import { SmartSortRotator } from '@iheartjane/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMemoizedRowInstance } from '@jane/bloom/hooks/dm/useMemoizedRowInstance';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import type { MenuTopRowResponse } from '@jane/bloom/routes/api.menu-top-row/types';
import { deriveBasename } from '@jane/bloom/utils/deriveBasename';
import { logError } from '@jane/bloom/utils/logError';

const DEFAULT_REFRESH_MS = 15_000;
const HOOK_NAME = 'useMenuTopRowWithServerData';

const MAX_CONSECUTIVE_FETCH_ERRORS = 3;

const HOVER = 'hover';

/** Carries the HTTP status to `onError`, the single place failures are logged. */
class MenuTopRowFetchError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message);
    this.name = 'MenuTopRowFetchError';
  }
}

const seedSnapshot = (
  row: SmartSort<MaterializedMenuProduct> | undefined
): SmartSortRotatorSnapshot<SmartSort<MaterializedMenuProduct>> => ({
  isError: false,
  isFetching: false,
  row,
});

interface UseMenuTopRowWithServerDataProps {
  /** Whether refetching is enabled. */
  enableRefetch?: boolean;
  /** Initial server-side menuTopRow data (serialized JSON string) */
  initialMenuTopRow?: string;
  /** Refresh time in seconds. Omit to use the store's configured ecomm cadence. */
  refreshTime?: number;
  /** Search filter string for client-side refetching. Required if enableRefetch is true. */
  searchFilter?: string;
  /** Store ID */
  storeId: number;
}

interface UseMenuTopRowWithServerDataResult {
  /** Whether there was an error */
  isError: boolean;
  /** Whether the query is currently fetching */
  isFetching: boolean;
  /** Whether the query was successful */
  isSuccess: boolean;
  /** Pause refetching */
  pauseRefetching: () => void;
  /** The current menu top row instance */
  rowInstance: SmartSort<MaterializedMenuProduct> | undefined;
  /** Unpause refetching */
  unpauseRefetching: () => void;
}

/**
 * Seeds the top-of-menu row from server-rendered data and rotates it on the
 * client. The rotation loop (timer, current-row hand-off, pause/resume,
 * self-terminate, request cancellation) lives in the framework-agnostic
 * {@link SmartSortRotator} in `@iheartjane/utils`; this hook only wires it to
 * React, derives the exclude list from the current row, and injects Bloom's
 * transport — a fetch to the same-origin `/api/menu-top-row` route, which keeps
 * the DM decisions call server-side (out of reach of CORS and ad blockers).
 */
export const useMenuTopRowWithServerData = ({
  enableRefetch = true,
  initialMenuTopRow,
  refreshTime,
  searchFilter,
  storeId,
}: UseMenuTopRowWithServerDataProps): UseMenuTopRowWithServerDataResult => {
  const { menuUrl, pinnedStore } = useRootLoaderData();
  // basename is applied to useFetcher but not raw fetch
  const basename = deriveBasename(menuUrl);

  const initialRow = useMemoizedRowInstance(initialMenuTopRow);

  const intervalMs =
    refreshTime !== undefined
      ? refreshTime * 1000
      : (pinnedStore?.menu_top_row_refresh_time?.['ecomm'] ??
        DEFAULT_REFRESH_MS);

  const [snapshot, setSnapshot] = useState<
    SmartSortRotatorSnapshot<SmartSort<MaterializedMenuProduct>>
  >(() => seedSnapshot(initialRow));

  // Reset the displayed row to the new seed when the server payload changes
  // (new route/filter), during render rather than in an effect.
  const [seedKey, setSeedKey] = useState(initialMenuTopRow);
  if (seedKey !== initialMenuTopRow) {
    setSeedKey(initialMenuTopRow);
    setSnapshot(seedSnapshot(initialRow));
  }

  // Holds the live rotator so pause/unpause can reach it without re-rendering.
  const rotatorRef = useRef<SmartSortRotator<
    SmartSort<MaterializedMenuProduct>
  > | null>(null);

  // The rotator is created and destroyed inside the effect (one instance per
  // run) so a remount — including React StrictMode's mount/unmount/mount — gets
  // a fresh, un-terminated instance rather than reusing a destroyed one.
  useEffect(() => {
    if (!enableRefetch || searchFilter === undefined || !initialRow) {
      rotatorRef.current = null;
      return;
    }

    const seed = initialRow;
    let transportFailureReported = false;
    const rotator = new SmartSortRotator<SmartSort<MaterializedMenuProduct>>({
      fetchNextSet: async ({ current, signal }) => {
        const excludeCreativeIds = current?.['creativeIds'] ?? [];
        const params = new URLSearchParams({
          searchFilter,
          storeId: String(storeId),
          ...(excludeCreativeIds.length > 0
            ? { excludeCreativeIds: JSON.stringify(excludeCreativeIds) }
            : {}),
        });

        let response: Response;
        try {
          response = await fetch(
            `${basename}/api/menu-top-row?${params.toString()}`,
            { signal }
          );
        } catch (error) {
          if (signal.aborted) throw error;
          // A network blip resolves to the current row, which the rotator reads as
          // a success — so it keeps the row on screen and retries next cycle, but
          // is never capped the way HTTP and parse failures are. Report the first
          // of a streak only, or losing connectivity logs once per cycle forever.
          if (!transportFailureReported) {
            transportFailureReported = true;
            logError(error, { hook: HOOK_NAME });
          }
          return current ?? seed;
        }

        // Throws below — including any from text()/json() — are logged by onError.
        if (!response.ok) {
          const body = await response.text();
          throw new MenuTopRowFetchError(
            body || `HTTP ${response.status}`,
            response.status
          );
        }

        const json = (await response.json()) as MenuTopRowResponse;
        if ('error' in json) {
          throw new MenuTopRowFetchError(json.error, response.status);
        }
        transportFailureReported = false;
        return SmartSort.fromJSON<MaterializedMenuProduct>(json.menuTopRow);
      },
      hasItems: (row) => row.products.length > 0,
      intervalMs,
      maxConsecutiveErrors: MAX_CONSECUTIVE_FETCH_ERRORS,
      onError: (error) =>
        logError(error, {
          hook: HOOK_NAME,
          ...(error instanceof MenuTopRowFetchError && {
            status: error.status,
          }),
        }),
      row: seed,
      stopOnEmpty: true,
    });

    rotatorRef.current = rotator;
    const unsubscribe = rotator.subscribe(() =>
      setSnapshot(rotator.getSnapshot())
    );
    rotator.start();

    return () => {
      // destroy() before unsubscribe() so its final isFetching: false reaches
      // React — otherwise tearing down mid-fetch leaves a stuck loading flag.
      rotator.destroy();
      unsubscribe();
      rotatorRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableRefetch,
    initialMenuTopRow,
    searchFilter,
    storeId,
    intervalMs,
    basename,
  ]);

  const pauseRefetching = useCallback(
    () => rotatorRef.current?.pause(HOVER),
    []
  );
  const unpauseRefetching = useCallback(
    () => rotatorRef.current?.resume(HOVER),
    []
  );

  return {
    isError: snapshot.isError,
    isFetching: snapshot.isFetching,
    isSuccess: !!snapshot.row && !snapshot.isError && !snapshot.isFetching,
    pauseRefetching,
    rowInstance: snapshot.row,
    unpauseRefetching,
  };
};
